export const COLUMN_SAVE_DELAY_MS = 1500;

export const STATIC_COLUMN_DEFAULTS = {
    category: true,
    published_at: true,
    first_event: true,
    source: true,
    tags: false,
    views: true,
    engagedViews: true,
    clicks: true,
    callBookings: true,
    emails: true,
    sales: true,
    revenue: true,
    clickPercentage: true,
    callBookingsPercentage: false,
    salesPercentage: true,
    emailsPercentage: false,
    emailsFromViewsPercentage: false,
    salesFromCallsPercentage: false,
    salesFromViewsPercentage: false,
    revenuePerView: false,
};

const BEFORE_FUNNEL = [
    'category',
    'published_at',
    'first_event',
    'source',
    'tags',
    'views',
    'engagedViews',
    'clicks',
    'callBookings',
];

const AFTER_FUNNEL_COUNTS = [
    'emails',
    'sales',
    'revenue',
    'clickPercentage',
    'callBookingsPercentage',
];

const AFTER_FUNNEL_RATES = [
    'salesPercentage',
    'emailsPercentage',
    'conversionPercentageFromViews',
    'callBookingConversionPercentage',
    'revenuePerView',
    'emailsFromViewsPercentage',
];

export function buildDefaultOrder(funnelSteps = []) {
    return [
        ...BEFORE_FUNNEL,
        ...funnelSteps.map((step) => `funnelStep_${step}`),
        ...AFTER_FUNNEL_COUNTS,
        ...funnelSteps.map((step) => `funnelStepPercentage_${step}`),
        ...AFTER_FUNNEL_RATES,
    ];
}

export function isColumnVisible(key, visible) {
    if (key.startsWith('funnelStepPercentage_')) return visible?.[key] === true;
    if (key.startsWith('funnelStep_')) return visible?.[key] !== false;
    return Boolean(visible?.[key]);
}

export function columnLabel(key, currencySymbol = '$') {
    if (key.startsWith('funnelStepPercentage_')) {
        return `${key.slice('funnelStepPercentage_'.length)} % from Clicks`;
    }
    if (key.startsWith('funnelStep_')) {
        return key.slice('funnelStep_'.length);
    }
    const labels = {
        category: 'Category',
        published_at: 'Published Date',
        first_event: 'First Event',
        source: 'Source',
        tags: 'Tags',
        views: 'Views',
        engagedViews: 'Engaged Views',
        clicks: 'Clicks',
        callBookings: 'Call Bookings',
        emails: 'Emails',
        sales: 'Sales',
        revenue: 'Revenue',
        clickPercentage: 'Clicks % from Views',
        callBookingsPercentage: 'Calls % from Clicks',
        salesPercentage: 'Sales % from Clicks',
        emailsPercentage: 'Emails % from Clicks',
        conversionPercentageFromViews: 'Sales % from Views',
        callBookingConversionPercentage: 'Sales % from Calls',
        revenuePerView: `${currencySymbol} per View`,
        emailsFromViewsPercentage: 'Emails % from Views',
    };
    return labels[key] || key;
}

// Place columns the account has never ordered into the same slot they occupy
// in the default layout, without moving keys the account already arranged.
export function mergeColumnOrder(savedOrder, defaultOrder) {
    const known = new Set(defaultOrder);
    const result = [];
    const seen = new Set();

    (Array.isArray(savedOrder) ? savedOrder : []).forEach((key) => {
        if (typeof key === 'string' && known.has(key) && !seen.has(key)) {
            result.push(key);
            seen.add(key);
        }
    });

    defaultOrder.forEach((key) => {
        if (seen.has(key)) return;
        const defaultIndex = defaultOrder.indexOf(key);
        let insertAt = result.length;
        let foundPrevious = false;

        for (let i = defaultIndex - 1; i >= 0; i -= 1) {
            const idx = result.indexOf(defaultOrder[i]);
            if (idx !== -1) {
                insertAt = idx + 1;
                foundPrevious = true;
                break;
            }
        }

        if (!foundPrevious) {
            for (let i = defaultIndex + 1; i < defaultOrder.length; i += 1) {
                const idx = result.indexOf(defaultOrder[i]);
                if (idx !== -1) {
                    insertAt = idx;
                    break;
                }
            }
        }

        result.splice(insertAt, 0, key);
        seen.add(key);
    });

    if (
        Array.isArray(savedOrder)
        && result.length === savedOrder.length
        && result.every((key, index) => key === savedOrder[index])
    ) {
        return savedOrder;
    }

    return result;
}

export function normalizeVisibleColumns(parsed) {
    const next = parsed && typeof parsed === 'object' ? { ...parsed } : {};
    if (Object.prototype.hasOwnProperty.call(next, 'conversions')) {
        next.sales = next.conversions;
        next.emails = next.conversions;
        delete next.conversions;
    }
    if (!Object.prototype.hasOwnProperty.call(next, 'engagedViews')) {
        next.engagedViews = true;
    }
    return next;
}

export function readLocalColumnPrefs() {
    let visible = { ...STATIC_COLUMN_DEFAULTS };
    let order = buildDefaultOrder([]);
    let hadSavedVisible = false;

    try {
        const savedColumns = localStorage.getItem('dashboardVisibleColumns');
        if (savedColumns) {
            visible = normalizeVisibleColumns(JSON.parse(savedColumns));
            hadSavedVisible = true;
        }
    } catch (error) {
        visible = { ...STATIC_COLUMN_DEFAULTS };
    }

    try {
        const savedOrder = localStorage.getItem('dashboardColumnOrder');
        if (savedOrder) {
            const parsed = JSON.parse(savedOrder);
            if (Array.isArray(parsed)) order = parsed;
        }
    } catch (error) {
        order = buildDefaultOrder([]);
    }

    return { visible, order, hadSavedVisible };
}
